import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CreateCustomerRequest } from '@/lib/api/customers';

import { addCustomerSchema, type AddCustomerInput } from './schema';

interface Props {
  onSave: (body: CreateCustomerRequest) => void;
  onClose: () => void;
  isSaving: boolean;
}

const DEFAULT_VALUES: AddCustomerInput = {
  fullName: '',
  email: '',
  phone: '',
  serviceAddress: '',
  notes: '',
};

export function AddCustomerModal({ onSave, onClose, isSaving }: Props) {
  const { t } = useLanguage();

  const form = useForm<AddCustomerInput>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(addCustomerSchema),
  });

  function handleSubmit(data: AddCustomerInput) {
    onSave({
      full_name: data.fullName,
      email: data.email || undefined,
      phone: data.phone || undefined,
      service_address: data.serviceAddress || undefined,
      notes: data.notes || undefined,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base">{t.addCustomerTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="modal-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="overflow-y-auto flex-1"
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            <Controller
              control={form.control}
              name="fullName"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="customer-name">{t.customerFullName}</Label>
                  <Input
                    {...field}
                    id="customer-name"
                    placeholder={t.customerFullNamePlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="customer-name-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />

            <Controller
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="customer-email">{t.customerEmail}</Label>
                  <Input
                    {...field}
                    id="customer-email"
                    type="email"
                    placeholder={t.customerEmailPlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="customer-email-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />

            <Controller
              control={form.control}
              name="phone"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="customer-phone">{t.employeePhone}</Label>
                  <Input
                    {...field}
                    id="customer-phone"
                    placeholder={t.phonePlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="customer-phone-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />

            <Controller
              control={form.control}
              name="serviceAddress"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="customer-service-address">{t.customerServiceAddress}</Label>
                  <Input
                    {...field}
                    id="customer-service-address"
                    placeholder={t.customerServiceAddressPlaceholder}
                    aria-invalid={fieldState.invalid}
                    data-testid="customer-service-address-input"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />

            <Controller
              control={form.control}
              name="notes"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="customer-notes">{t.customerNotes}</Label>
                  <textarea
                    {...field}
                    id="customer-notes"
                    placeholder={t.customerNotesPlaceholder}
                    rows={3}
                    aria-invalid={fieldState.invalid}
                    data-testid="customer-notes-input"
                    className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t.cancel}
            </Button>
            <Button type="submit" size="sm" disabled={isSaving} data-testid="save-customer">
              {t.saveCustomer}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
